import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import useBranches from "@/hooks/useBranches";
import { Plus, PencilSimple, Trash, X, IdentificationCard } from "@phosphor-icons/react";

const BLANK = { name: "", phone: "", email: "", code: "", branch_id: "", active: true };

function AgentFormDrawer({ open, editing, branches, onClose, onSaved }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? { ...BLANK, ...editing } : BLANK);
  }, [open, editing]);

  if (!open) return null;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.branch_id) {
      toast.error("Branch is required — agents are now tied to a specific branch.");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, branch_id: form.branch_id };
      if (editing) await api.patch(`/sales-agents/${editing.id}`, body);
      else await api.post("/sales-agents", body);
      toast.success(editing ? "Agent updated" : "Agent created");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-ink/40" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
        data-testid="agent-form-drawer"
        className="w-[440px] bg-canvas-card border-l-2 border-ink overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-canvas-line flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-muted">
              {editing ? "Edit Agent" : "New Agent"}
            </div>
            <h2 className="font-display text-xl font-bold text-ink mt-1">
              {editing ? editing.name : "Sales agent"}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 border border-canvas-line hover:border-ink">
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {[
            ["Name", "name", true, ""],
            ["Phone", "phone", false, ""],
            ["Email", "email", false, ""],
            ["Employee Code", "code", false, "e.g., AG-001"],
          ].map(([label, key, required, placeholder]) => (
            <div key={key}>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                {label}
                {required && <span className="text-destructive ml-1">*</span>}
              </label>
              <input
                value={form[key] || ""}
                onChange={(e) => set(key)(e.target.value)}
                required={required}
                placeholder={placeholder}
                data-testid={`agent-${key}`}
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              />
            </div>
          ))}
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Branch <span className="text-rose-600">*</span>
            </label>
            <select
              value={form.branch_id || ""}
              onChange={(e) => set("branch_id")(e.target.value)}
              data-testid="agent-branch"
              required
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="">— Select a branch —</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <div className="font-mono text-[10px] text-ink-subtle mt-1">
              Agents only appear in sale/booking pickers for this branch.
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.active !== false}
              onChange={(e) => set("active")(e.target.checked)}
              data-testid="agent-active"
              className="accent-ink"
            />
            <span className="font-mono text-xs uppercase tracking-wider text-ink-muted">
              Active (can be selected on new sales)
            </span>
          </label>
          <div className="pt-4 border-t border-canvas-line flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 border border-canvas-line text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="agent-save"
              className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Save" : "Create agent"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function SalesAgentsPage() {
  const [agents, setAgents] = useState([]);
  const { branches } = useBranches();
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales-agents");
      setAgents(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const branchById = useMemo(
    () => Object.fromEntries((branches || []).map((b) => [b.id, b])),
    [branches],
  );

  const onDelete = async (agent) => {
    if (!window.confirm(`Delete agent "${agent.name}"?`)) return;
    try {
      const { data } = await api.delete(`/sales-agents/${agent.id}`);
      toast.success(data.deactivated
        ? `Marked inactive (used by ${data.in_use} sale${data.in_use === 1 ? "" : "s"})`
        : "Agent deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="sales-agents-page">
      <PageHeader
        eyebrow="12 / Sales"
        title="Sales Agents"
        subtitle="The people whose names appear on each sale & invoice. Independent of system users."
        actions={
          <button
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
            data-testid="add-agent-button"
            className="bg-ink text-white px-4 py-2.5 text-sm font-semibold tracking-tight hover:bg-brand transition-colors duration-150 flex items-center gap-2"
          >
            <Plus size={14} weight="bold" />
            New Agent
          </button>
        }
      />

      <div className="px-8 py-6">
        <div className="bg-canvas-card border border-canvas-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="agents-table">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  {["Name", "Code", "Phone", "Email", "Branch", "Status", ""].map((h) => (
                    <th
                      key={h || "act"}
                      className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center font-mono text-xs text-ink-subtle">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && agents.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <IdentificationCard size={24} weight="bold" className="text-ink-subtle mx-auto mb-2" />
                      <div className="font-mono text-xs text-ink-subtle">No agents yet. Add the first one to start recording sales.</div>
                    </td>
                  </tr>
                )}
                {agents.map((a) => (
                  <tr
                    key={a.id}
                    data-testid={`agent-row-${a.id}`}
                    className="border-b border-canvas-line hover:bg-canvas-panel/40"
                  >
                    <td className="px-4 py-3 font-semibold text-ink">
                      {a.name}
                      {a.is_placeholder && (
                        <span className="ml-2 px-2 py-0.5 border border-ink/30 text-[9px] font-mono uppercase tracking-wider text-ink-muted">
                          Placeholder
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{a.code || "—"}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{a.phone || "—"}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-ink-muted">{a.email || "—"}</td>
                    <td className="px-4 py-3 text-sm text-ink-muted">
                      {a.branch_id ? branchById[a.branch_id]?.name || "—" : "All"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 border text-[10px] font-mono uppercase tracking-wider ${
                          a.active
                            ? "border-[#00B341] bg-[#00B341]/10 text-[#00B341]"
                            : "border-canvas-line text-ink-subtle"
                        }`}
                      >
                        {a.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => {
                            setEditing(a);
                            setDrawerOpen(true);
                          }}
                          data-testid={`edit-agent-${a.id}`}
                          className="p-1.5 border border-canvas-line hover:border-ink"
                        >
                          <PencilSimple size={12} weight="bold" />
                        </button>
                        {!a.is_placeholder && (
                          <button
                            onClick={() => onDelete(a)}
                            data-testid={`delete-agent-${a.id}`}
                            className="p-1.5 border border-destructive text-destructive hover:bg-destructive hover:text-white"
                          >
                            <Trash size={12} weight="bold" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AgentFormDrawer
        open={drawerOpen}
        editing={editing}
        branches={branches || []}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          setDrawerOpen(false);
          load();
        }}
      />
    </div>
  );
}
